import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// `/pure` avoids @stripe/stripe-js auto-injecting js.stripe.com (~259 KiB) on import.
// Stripe.js now loads only when loadStripe() is called (at the payment step), not on
// the initial checkout. Trade-off: Radar fraud fingerprinting starts at payment, not page load.
import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../../../Shared/CheckoutForm";
import { order_post } from "api/orders";
import { Alert } from "antd";
import CustomButton from "Components/CustomButton";
import Spin from "Components/Spinner";
import SelectPaymentMethodModal from "../../../Shared/SelectPaymentMethodModal";
import { ReloadOutlined } from "@ant-design/icons";
import {
  payment_stripe_publishable_key_get,
  payment_stripe_create_intent_post,
  payment_stripe_intent_confirm_post,
  stripeV4CreatePaymentSession,
} from "api/payments";

import { utmParams } from "utils/helper";
import { getStripeLocale } from "i18n/helpers";
const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } = utmParams();

/***********************************************************************************************
 ***********************************************************************************************
 ***********************************************************************************************
 * ##: APP
 ***********************************************************************************************
 ***********************************************************************************************
 **********************************************************************************************/
const Stripe = ({ productData, data, actionHandler, step, method, customStyle, setShowChangeMethod }) => {
  const { t } = useTranslation();
  const stripeLocale = getStripeLocale();
  //const [clientSecret, setClientSecret] = useState("");
  //const [intentId, setIntentId] = useState("");
  const [stripeData, setStripeData] = useState({ client_secret: "", intent_id: "", payment_unique_id: "" });
  const [publishableKey, setPublishableKey] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFetching, setErrorFetching] = useState(false);
  const [selectPaymentMethod, setSelectPaymentMethod] = useState({ visible: false });
  const [stripeSessionId, setStripeSessionId] = useState(null);

  // const appearance = {
  //   theme: "stripe",
  // };
  // const options = {
  //   clientSecret,
  //   appearance,
  // };
  useEffect(() => {
    if (publishableKey === "") prepareCheckout();
  }, []);

  useEffect(() => {
    // Only show the "change method" link once the card form is ready (not loading).
    const show = stripeData.client_secret !== "" && !errorFetching && !isLoading;
    if (setShowChangeMethod) setShowChangeMethod(show);
    return () => {
      if (setShowChangeMethod) setShowChangeMethod(false);
    };
  }, [stripeData.client_secret, errorFetching, isLoading]);

  const prepareCheckout = async () => {
    if (data?.identification?.email === undefined || data?.identification?.email === null) {
      return;
    }

    setIsLoading(true);
    setErrorFetching(false);
    // Get Stripe publishable key
    const pub_key_response = await getPublishableKey(productData.product_id);
    // Check if there is an error
    if (!pub_key_response.status) {
      setErrorFetching(true);
      setIsLoading(false);
      return;
    }

    // Set publishable key and load Stripe
    setPublishableKey(pub_key_response.data.publishable_key);
    setStripePromise(() => loadStripe(pub_key_response.data.publishable_key));
    // stripePromise = loadStripe(pub_key_response.data.publishable_key);
    // Create payload
    const payload = {
      buyer: data.identification,
      products: data.purchase_products,
      purchase_id: data.purchase_id,
      main_product: data.main_product,
      coupon: data.coupon,
      pathname: window.location.pathname,
      origin: window.location.origin,
      affiliate_id: data.affiliate_id,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      payment_method: method,
      source: "checkout",
      free_resolution_waiver: data.identification?.free_resolution_checkbox || false,
      is_offer: data.identification?.is_offer || false,
      offer_info: {
        gift_name: data.identification?.gift_name || "",
        gift_email: data.identification?.gift_email || "",
        gift_message: data.identification?.gift_message || "",
        gift_delivery_date: data.identification?.gift_delivery_date || "",
      },
    };

    // Create Order
    const order_response = await _createOrder(payload);
    // Check if there is an error
    if (!order_response.status) {
      setIsLoading(false);
      setErrorFetching(true);
      return;
    }

    // const create_intent_response = await _createPaymentIntent(payload);
    // // Check if there is an error
    // if (!create_intent_response.status) {
    //   setIsLoading(false);
    //   setErrorFetching(true);
    //   return;
    // }

    const createCheckoutSessionResponse = await createPaymentSession(payload);
    // Check if there is an error
    if (!createCheckoutSessionResponse.status) {
      setIsLoading(false);
      setErrorFetching(true);
      return;
    }

    setStripeSessionId(createCheckoutSessionResponse.data.session_id);

    setStripeData({
      session_id: createCheckoutSessionResponse.data.session_id,
      customer: createCheckoutSessionResponse.data.customer,
      success_url: createCheckoutSessionResponse.data.success_url,
      setup_intent: createCheckoutSessionResponse.data.setup_intent,
      client_secret: createCheckoutSessionResponse.data.client_secret,
      payment_unique_id: createCheckoutSessionResponse.data.payment_unique_id,
    });

    setIsLoading(false);
  };

  const _createOrder = (payload) => {
    return new Promise((resolve) => {
      try {
        order_post(payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const _createPaymentIntent = (payload) => {
    return new Promise((resolve) => {
      try {
        payment_stripe_create_intent_post(payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const createPaymentSession = (payload) => {
    return new Promise((resolve) => {
      try {
        // Disable hability to change quantity
        actionHandler("qty_locked", true);

        stripeV4CreatePaymentSession(payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  // const _confirmPaymentIntent = (payload) => {
  //   return new Promise((resolve) => {
  //     try {
  //       payment_stripe_intent_confirm_post(payload)
  //         .then((res) => resolve({ status: true, data: res.data }))
  //         .catch((err) => resolve({ status: false, data: err }));
  //     } catch (error) {
  //       resolve({ status: false, data: error });
  //     }
  //   });
  // };

  const paymentCompleted = () => {
    actionHandler("update_status", {
      action: "wait-for-payment",
      data: {
        method_id: "CreditCard",
        entity: "00000",
        reference: stripeData.intent_id,
      },
    });
  };

  // const confirmPaymentIntent = (payload) => {
  //   return new Promise((resolve) => {
  //     try {
  //       confirmPaymentIntentV4(payload)
  //         .then((res) => resolve({ status: true, data: res.data }))
  //         .catch((err) => resolve({ status: false, data: err }));
  //     } catch (error) {
  //       resolve({ status: false, data: error });
  //     }
  //   });
  // };

  const paymentIntentConfirm = (payload) => {
    return new Promise((resolve) => {
      try {
        payment_stripe_intent_confirm_post(payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const getPublishableKey = (product_id) => {
    return new Promise((resolve) => {
      try {
        payment_stripe_publishable_key_get(product_id)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  return (
    <>
      <div className="credit-card-container">
        <div className="payment-data-container">
          <Spin spinning={isLoading} tip={t(method === "klarna" ? "klarna.loadingTip" : "stripe.loadingTip")}>
            {errorFetching && (
              <div className="error">
                <Alert
                  message={t("stripe.fetchErrorTitle")}
                  description={t("stripe.fetchErrorDescription")}
                  type="error"
                  showIcon
                />
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="primary" onClick={() => prepareCheckout()}>
                    {t("stripe.retryButton")} <ReloadOutlined />
                  </CustomButton>
                </div>
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="link" onClick={() => setSelectPaymentMethod({ visible: true })}>
                    {t("stripe.changeMethodButton")}
                  </CustomButton>
                </div>
              </div>
            )}
            {stripeData.client_secret !== "" && (
              <>
                <Elements
                  key={`${stripeData.client_secret}-${stripeLocale}`}
                  options={{
                    //   customerOptions: "",
                    appearance: { theme: "stripe" },
                    clientSecret: stripeData.client_secret,
                    locale: stripeLocale,
                  }}
                  stripe={stripePromise}
                >
                  <CheckoutForm
                    data={data}
                    actionHandler={actionHandler}
                    stripeData={stripeData}
                    paymentCompleted={paymentCompleted}
                    customStyle={customStyle}
                  />
                </Elements>
              </>
            )}
          </Spin>
        </div>
      </div>
      <SelectPaymentMethodModal
        data={data}
        actionHandler={actionHandler}
        visible={selectPaymentMethod.visible}
        close={() => setSelectPaymentMethod({ visible: false })}
        customStyle={customStyle}
      />
    </>
  );
};

export default Stripe;
